import { useEffect, useState } from "react";
import { getAgent, runAgent, buildAgent, getAgentRunResultFromUrl } from "../api/agentApi";
import { useParams, useNavigate, Link as RouterLink } from "react-router-dom";
import { Box, Typography, Paper, CircularProgress, Alert, Button, Snackbar, Chip,
  TextField, Accordion, AccordionSummary, AccordionDetails, Link, Tabs, Tab } 
  from "@mui/material";
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { marked } from "marked";
import GeneralTabPanel from "./AgentDetail/GeneralTabPanel.jsx";
import AgentRuns from "./AgentDetail/AgentRuns.tsx";
import DetailItem from './AgentDetail/DetailItem.tsx';
import AgentRunDrawer from "./AgentDetail/AgentRunDrawer.tsx";

const POLL_INTERVAL_BASE = 5000; // ms
const POLL_INTERVAL_MAX = 60000; // ms

export default function NewAgentDetailPage() {
  const { agentId } = useParams();
  const navigate = useNavigate();
  const [agent, setAgent] = useState(null);
  const [llmSettings, setLLMSettings] = useState(undefined);
  const [error, setError] = useState("");
  const [runStatus, setRunStatus] = useState(null);
  const [buildStatus, setBuildStatus] = useState(null);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [prompt, setPrompt] = useState("");
  const [openaiApiKey, setOpenaiApiKey] = useState("");
  const [isRunning, setIsRunning] = useState(false);
  const [isBuilding, setIsBuilding] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [runsRefreshToken, setRunsRefreshToken] = useState(0);
  const [value, setValue] = useState(0);
  // Final result polling UI state
  const [finalResult, setFinalResult] = useState("");
  const [isPolling, setIsPolling] = useState(false);
  const [thinkingDots, setThinkingDots] = useState(0);
  
  const handleChange = (event, newValue) => {
    setValue(newValue);
  };

  function a11yProps(index) { return { id: `tab-${index}`, 'aria-controls': `tabpanel-${index}` }; }

  useEffect(() => {
      let poller;
      let isUnmounted = false;
      const fetchAgent = async () => {
        try {
          const data = await getAgent(agentId);
          if (!isUnmounted) {
            setAgent(data);
            setError("");
            // Only poll every 5s if status is 'creating' or 'building'
            if (data.status === "creating" || data.status === "building") {
              poller = setTimeout(fetchAgent, POLL_INTERVAL_BASE);
            }
            // No polling for other statuses
          }
        } catch {
          if (!isUnmounted) setError("Failed to load agent details.");
        }
      };
      fetchAgent();
      return () => {
        isUnmounted = true;
        if (poller) clearTimeout(poller);
      };
    }, [agentId]);
      // Use agent object directly (API now returns JSON)
      const card = agent;

    useEffect(() => {
      if (card) {
        const parsedObject = JSON.parse(card?.llm_settings || '{}');
        setLLMSettings(parsedObject);
      }
    }, [card]);

  const handleRun = async () => {
    setRunStatus(null);
    setIsRunning(true);
    setFinalResult("");
    setIsPolling(false);
    try {
      const result = await runAgent(
        agentId,
        JSON.stringify({ input: prompt }) + "\n",
        openaiApiKey
      );
      setRunStatus(result);
      setSnackbarOpen(true);
      // Refresh agent after successful run to update number_of_executions
      if (result.status === "success") {
        const updated = await getAgent(agentId);
        setAgent(updated);
      }
    } catch (e) {
      setRunStatus({ status: "error", message: "Failed to run agent." });
      setSnackbarOpen(true);
    } finally {
      setIsRunning(false);
    }
  };
  
  // Poll for final result after agent run
  useEffect(() => {
    let pollTimeout;
    let dotsInterval;
    let isUnmounted = false;
    
    if (runStatus && runStatus.result_endpoint) {
      setIsPolling(true);
      setFinalResult("");
      setThinkingDots(0);
      
      // Animate 'Thinking...' ellipsis (0 to 3 dots)
      dotsInterval = setInterval(() => {
        setThinkingDots(prev => (prev + 1) % 4);
      }, 500);
      
      const pollResult = async () => {
        if (isUnmounted) return;
        
        try {
          const data = await getAgentRunResultFromUrl(runStatus.result_endpoint);
          
          if (data.status === "success" && typeof data.output === "string") {
            const idx = data.output.indexOf("Final Answer");
            if (idx !== -1) {
              // Extract everything from "Final Answer" to the end
              let answer = data.output.substring(idx);
              // Remove "Final Answer" prefix and any following colon/newlines/whitespace
              // This handles both "Final Answer: text" and "Final Answer:\ntext"
              answer = answer.replace(/^Final Answer\s*:?\s*\n?/i, '').trim();
              setFinalResult(answer);
              setIsPolling(false);
              clearInterval(dotsInterval);
              return;
            }
          }
        } catch (err) {
          console.error("Error polling result:", err);
        }
        
        // Continue polling
        pollTimeout = setTimeout(pollResult, 2000);
      };
      
      pollResult();
    }
    
    return () => {
      isUnmounted = true;
      if (pollTimeout) clearTimeout(pollTimeout);
      if (dotsInterval) clearInterval(dotsInterval);
    };
  }, [runStatus && runStatus.result_endpoint]);

   const handleBuild = async () => {
      setBuildStatus(null);
      setIsBuilding(true);
      // Immediately set agent status to 'building' for UI feedback and polling
      setAgent(prev => prev ? { ...prev, status: "building" } : prev);
      setError(""); // Clear any previous error
      // Start polling immediately after setting status
      const poller = setTimeout(async function poll() {
        try {
          const data = await getAgent(agentId);
          setAgent(data);
          if (data.status === "building") {
            setTimeout(poll, POLL_INTERVAL_BASE);
          }
        } catch {
          setError("Failed to load agent details.");
        }
      }, POLL_INTERVAL_BASE);
      try {
        const result = await buildAgent(agentId);
        setBuildStatus(result);
        setSnackbarOpen(true);
      } catch (e) {
        setBuildStatus({ status: "error", message: "Failed to build agent." });
        setSnackbarOpen(true);
      } finally {
        setIsBuilding(false);
      }
    };
  

  const handleRunAgent = () => {
    setIsOpen(true)
  }

  if (error) return <Alert severity="error">{error}</Alert>;
  if (!agent) return <Box sx={{ display: "flex", justifyContent: "center", mt: 8 }}><CircularProgress /></Box>;




  const handleEdit = () => {
    // Always pass the latest card as state for pre-population
    navigate("/agent/conversational", { state: { agentCard: card } });
  };
  
  return (
    <Box>
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2, pr: 3, mt: 2 }}>
            <Typography variant="h4" gutterBottom>Agent: {agent.display_name}</Typography>
                <Box sx={{ display: "flex", gap: 2 }}>
                    <Button variant="outlined" color="primary" onClick={handleEdit}
                        sx={{borderRadius: "99px", textTransform: 'none'}}>
                    Edit
                     </Button>
                    <Button
                      variant="contained"
                      color="primary"
                      onClick={handleBuild}
                      sx={{ borderRadius: "99px", textTransform: 'none', color: "primary", display: 'none' }}
                    >
                      Build Agent
                    </Button>
                    <Button variant="contained" color="primary" onClick={handleRunAgent} disabled={card.status !== "ready"}
                        sx={{borderRadius: "99px", textTransform: 'none', color: "primary"}}
                    > 
                    {"Run Agent"}
                    </Button>
                </Box>
        </Box>
       <Box sx={{ width: '100%', borderBottom: 1, borderColor: 'divider' }}>
          <Tabs value={value} onChange={handleChange} aria-label="dashboard tabs">
              <Tab sx={{ textTransform: 'none' }} label="Agent Details" {...a11yProps(0)} />
              <Tab sx={{ textTransform: 'none' }} label="Agent Run History" {...a11yProps(1)} />
              
              {/* <Tab sx={{ textTransform: 'none' }} label="Instance Details" {...a11yProps(3)} /> */}
          </Tabs>
        </Box>
      {/* List style agent info */}
      <GeneralTabPanel value={value} index={0} sx={{ p: 0 }}>
      <Box sx={{ mb: 0, p: 2 }}>
        
        {/* <AgentPromptAndResponse /> */}
        <DetailItem label="Role" value={card.role} />
        <DetailItem label="Agent ID" value={card.agent_id} />
        <DetailItem label="Display Name" value={card.display_name} />
        <DetailItem label="Description" 
                    value={card.description} />
        <DetailItem label="Owner" value={card.owner || "Unknown"} />
        <DetailItem label="Status" value={
              <Chip
                    label={agent.status || "Unverified"}
                    sx={{
                      bgcolor: agent.status === "ready"
                        ? "#43a047"
                        : agent.status === "building"
                          ? "#ffb300"
                          : agent.status === "created"
                            ? "#3f98f2ff"
                            : "#e0e0e0",
                      color: agent.status === "ready" ? "#fff" : "#333",
                      fontWeight: 500,
                      borderRadius: "9999px", // fully rounded
                      height: 24,
                      px: 1.5,
                    }}
                />
            }
        />
        <DetailItem label="Number of Executions" value={card.number_of_executions ?? 0} />
        <DetailItem label="Tools" value={
          <>
          {Array.isArray(card.tools) && card.tools.length > 0 ? (
            
            <Box sx={{}}>
              {card.tools.map((tool, idx) => (
                <Box key={idx} sx={{ }}>
                  <Typography variant="body1" sx={{ fontWeight: 500 }}>{tool.name || tool.tool_name || `Tool ${idx + 1}`}</Typography>
                  {tool.description && (
                    <Typography variant="caption" sx={{ color: '#666' }}>{tool.description}</Typography>
                  )}
                  {tool.type && (
                    <Typography variant="caption" sx={{ color: '#888', ml: 1 }}>Type: {tool.type}</Typography>
                  )}
                </Box>
              ))}
            </Box>)
            : (
            <Typography variant="body2" sx={{ mb: 2, color: '#888' }}>No tools found for this agent.</Typography>
            )}
          </>}
        />
        
        <DetailItem label="LLM" value={card.llm} />
        <DetailItem label="LLM Temperature" value={llmSettings?.temperature} />
        <DetailItem label="LLM Maximum tokens" value={llmSettings?.max_tokens} />
        <DetailItem label="Created" value={card.date || ""} />    
        
         <DetailItem label="Associated Collections" value={
          <>
            {Array.isArray(card.rag) && card.rag.length > 0 ? (
            <Box sx={{ mb: 2, pl: 2 }}>
              {card.rag.map((index, idx) => (
                <Box key={index.id || idx} sx={{ mb: 1, p: 1, bgcolor: '#e8f5e9', borderRadius: 1 }}>
                  <Typography variant="body1" sx={{ fontWeight: 500 }}>
                    {index.name || `Index ${idx + 1}`}
                  </Typography>
                  <Typography variant="caption" sx={{ color: '#666' }}>
                    ID: {index.id}
                  </Typography>
                  {index.rag_type && (
                    <Typography variant="caption" sx={{ color: '#888', ml: 1 }}>
                      Type: {index.rag_type}
                    </Typography>
                  )}
                </Box>
              ))}
            </Box>
            ) : (
              <Typography variant="body1" sx={{ mb: 2, color: '#888' }}>No collections associated with this agent.</Typography>
            )}
          </>
         }
        />
        
        {/* <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>Artifacts</Typography>
        <Box sx={{ display: "flex"}}>
          <Link 
            component={RouterLink}
            to={`/agent/${agent.agent_id}/artifacts`} 
            tyle={{ textDecoration: "none", color: "#1976d2", fontWeight: 500 }}>
              {"View Details"}
          </Link>
      </Box> */}
      
      </Box>
      {/* Build status messages */}
      {/* Build status messages related to 'building' are now removed */}
      {card.status === "ready" && (
        <Alert severity="success" sx={{ mt: 0}}>Agent is now Ready for Execution!</Alert>
      )}
      {card.status === "failed" && (
        <Alert severity="error" sx={{ mt: 0 }}>Agent build failed: {card.message}</Alert>
      )}
      <TextField
        label="Prompt"
        value={prompt}
        onChange={e => setPrompt(e.target.value)}
        fullWidth
        multiline
        minRows={2}
        sx={{ mt: 3, bgcolor: card.status !== "ready" ? '#f0f0f0' : undefined }}
        placeholder='Describe your task here...'
        disabled={card.status !== "ready"}
      />
      {/* <TextField
        label="OpenAI API Key (optional)"
        value={openaiApiKey}
        onChange={e => setOpenaiApiKey(e.target.value)}
        fullWidth
        sx={{ mt: 2 }}
        placeholder="sk-..."
        type="password"
      /> */}
      <Button variant="contained" color="primary" sx={{ mt: 2, borderRadius: "99px" }} onClick={handleRun} disabled={isRunning || card.status !== "ready"}>
        {isRunning ? 'Running...' : 'Run Agent'}
      </Button>
      {isRunning && (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
          <CircularProgress size={32} />
        </Box>
      )}
      
      {/* Final result polling UI */}
      {isPolling && (
        <Box sx={{ mt: 2, p: 2, borderRadius: 1 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>Result</Typography>
          <Box sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word', m: 0 }}>
            <Box sx={{ fontSize: 18, color: '#555', fontWeight: 500 }}>
              Thinking{'.'.repeat(thinkingDots)}
            </Box>
          </Box>
        </Box>
      )}
      
      {!isPolling && finalResult && (
        <Box sx={{ mt: 2, p: 2, borderRadius: 1 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>Final Answer</Typography>
          <Box sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word', m: 0 }}>
            <Box sx={{
              '& p': { mt: 0, mb: 1 },
              '& ul, & ol': { mt: 0, mb: 0.5, pl: 3 },
              '& li': { mb: 0.5 },
              '& pre': { bgcolor: '#f5f5f5', p: 1, borderRadius: 1, overflow: 'auto' },
              '& code': { bgcolor: '#f5f5f5', px: 0.5, borderRadius: 0.5, fontSize: '0.9em' }
            }}>
              <span dangerouslySetInnerHTML={{ __html: marked.parse(finalResult) }} />
            </Box>
          </Box>
        </Box>
      )}
      
      {runStatus && runStatus.output && (
        <Accordion sx={{ mt: 2, borderRadius: 1, boxShadow: 'none' }}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>Output (raw agent output)</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Box component="pre" sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word', m: 0 }}>
              {runStatus.output}
            </Box>
          </AccordionDetails>
        </Accordion>
      )}
      {runStatus && runStatus.status === "error" && (runStatus.message || runStatus.output) && (
        <Accordion sx={{ mt: 2, background: '#ffebee', borderRadius: 1, boxShadow: 'none' }}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#b71c1c' }}>Error (click to expand)</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Box component="pre" sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word', m: 0, color: '#b71c1c' }}>
              {runStatus.message}
              {runStatus.output && (typeof runStatus.output === 'string' ? `\n${runStatus.output}` : '')}
            </Box>
          </AccordionDetails>
        </Accordion>
      )}
      {buildStatus && buildStatus.status === "error" && card.status !== "building" && (
        <Accordion sx={{ mt: 2, background: '#ffebee', borderRadius: 1, boxShadow: 'none' }}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
              Build Error
            </Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Box component="pre" sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word', m: 0 }}>
              {buildStatus.output || buildStatus.message}
            </Box>
          </AccordionDetails>
        </Accordion>
      )}
      {buildStatus && buildStatus.status === "success" && (
        <Accordion sx={{ mt: 2, background: '#e3f2fd', borderRadius: 1, boxShadow: 'none' }}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
              Build Output
            </Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Box component="pre" sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word', m: 0 }}>
              {buildStatus.output || buildStatus.message}
            </Box>
          </AccordionDetails>
        </Accordion>
      )}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={4000}
        onClose={() => setSnackbarOpen(false)}
        message={
          (buildStatus && buildStatus.status === "started" && "Agent build started.") ||
          (buildStatus && buildStatus.status === "success" && "Agent built successfully.") ||
          (buildStatus && buildStatus.status === "error" && card.status !== "building" && buildStatus.message) ||
          (runStatus && runStatus.status === "success"
            ? `Agent running. Container ID: ${runStatus.container_id}`
            : runStatus && runStatus.status === "error" && runStatus.message)
        }
      />
      </GeneralTabPanel>
    <GeneralTabPanel value={value} index={1} sx={{ p: 0 }}>
      <AgentRuns agentId={card.agent_id} agentVersion={card.version} refreshToken={runsRefreshToken} />
    </GeneralTabPanel>
      
      <AgentRunDrawer
        agentCard={agent}
        agentID={card.agent_id}
        agentName={card.name}
        open={isOpen}
        onClose={() => setIsOpen(false)}
        onRunComplete={() => setRunsRefreshToken((prev) => prev + 1)}
      />
    </Box>
  );
}